import { encoding_for_model } from "@dqbd/tiktoken";
import { ContentType } from "@/view/constants/contentTypes";
import articleTypes from "@/view/constants/articleTypes";
import contentTypes from "@/view/constants/contentTypes";
import { ContentLevel } from "@/types/models/ContentLevel.interface";
import { IMetadata, isDecodableContent } from "@/view/utils/decodableContent";
import { LANG_EN } from "@/constants/Language";
// OpenAI Model that we specify on the BE when we query GPT.
export const OPEN_AI_MODEL = "text-davinci-003";
// A maximum number of tokens allowed for text-davinci-003 model.
export const OPEN_AI_MODEL_MAX_TOKENS = 4097;
// A limit on the maximum prompt size that we specified on the BE.
// This an arbitrary number selected by trial and error.
export const OPEN_AI_RESPONSE_MAX_TOKENS = 500;
// Size of the largest tokenized teacher tool prompt. There are several.
export const LARGEST_TT_PROMPT_NUMBER_OF_TOKENS = 245;
// Size of the tokenized WritePrompt prompt.
export const WRITE_PROMPT_NUMBER_OF_TOKENS = 496;

export const MAX_ALLOWED_TT_TOKENS =
  OPEN_AI_MODEL_MAX_TOKENS -
  OPEN_AI_RESPONSE_MAX_TOKENS -
  LARGEST_TT_PROMPT_NUMBER_OF_TOKENS;
export const MAX_ALLOWED_WRITE_PROMPT_TOKENS =
  OPEN_AI_MODEL_MAX_TOKENS -
  OPEN_AI_RESPONSE_MAX_TOKENS -
  WRITE_PROMPT_NUMBER_OF_TOKENS;

const { INTERACTIVE_VIDEO } = contentTypes;

interface IUnsupportedContentParams {
  articleType?: string | undefined;
  contentType: ContentType;
  text: string;
  contentMetadata: IMetadata[];
}

interface IsSupportedEnglishContentParams {
  articleType?: string | undefined;
  currentLevel?: ContentLevel | undefined;
  contentMetadata: IMetadata[];
  contentType: ContentType;
}

/* Determines if content is eligible for Exportable Content or Lesson Planner features. */
export function isSupportedEnglishContent({
  articleType = "",
  contentType,
  currentLevel,
  contentMetadata,
}: IsSupportedEnglishContentParams): boolean {
  const { language } = currentLevel || {};
  const isEnglishContent = language === LANG_EN;

  return (
    !isVisualContent({ contentType, articleType }) &&
    !isDecodableContent(contentMetadata) &&
    isEnglishContent
  );
}

export const isVisualContent = ({
  contentType,
  articleType = "",
}: {
  contentType: ContentType;
  articleType?: string;
}) => {
  return (
    contentType === INTERACTIVE_VIDEO ||
    [
      articleTypes.ARTICLE_INFOGRAPHIC,
      articleTypes.ARTICLE_MAP,
      articleTypes.ARTICLE_PHOTO,
    ].includes(articleType)
  );
};

function isUnsupportedContentForTeacherTools({
  articleType = "",
  contentType,
  text,
  contentMetadata,
}: IUnsupportedContentParams): boolean {
  return (
    isVisualContent({ contentType, articleType }) ||
    isTextTooLargeToProcessForTeacherTools(text) ||
    isDecodableContent(contentMetadata)
  );
}

function isTextTooLargeToProcess(
  text: string,
  maxAllowedTokens: number,
): boolean {
  // By subtracting prompt token size and response token size from the maximum allowed number of tokens for the `model`
  // we find the maximum allowed number of tokens for the article that can be sent to OpenAI API.
  if (!text) {
    return false;
  }
  const encoding = encoding_for_model(OPEN_AI_MODEL);
  const articleTokens = encoding.encode(text);
  // If article is too large, there is nothing we can do on the FE, so we hide Teacher Tools for now to avoid
  // making API calls that we know would fail.
  const articleIsTooLarge = articleTokens?.length > maxAllowedTokens;
  encoding.free();
  return articleIsTooLarge;
}

function isTextTooLargeToProcessForWritePrompts(text: string): boolean {
  return isTextTooLargeToProcess(text, MAX_ALLOWED_WRITE_PROMPT_TOKENS);
}

function isTextTooLargeToProcessForTeacherTools(text: string): boolean {
  return isTextTooLargeToProcess(text, MAX_ALLOWED_TT_TOKENS);
}

export {
  isTextTooLargeToProcessForTeacherTools,
  isTextTooLargeToProcessForWritePrompts,
  isUnsupportedContentForTeacherTools,
};
